/* eslint-disable react-hooks/exhaustive-deps */
import { SubscribeModal, VerifyEmailModal } from '@/components/modal'

import '@/config/branchio'
import '@/config/mixpanel'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/remote-config'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'

import { userSelector } from '@/pages/user/selectors'

import { useEffect } from 'react'

import { RCPurchaseController } from './config/RCPurchaseController'
import { resolveRcAppUserId, fetchBackendEntitlementMarkers } from './services/rcIdentity'
import { setEntitlements } from './app/entitlementsActions'
import { AppRoutes } from './AppRoutes'
import { useSetBanner } from './hooks/useSetBanner'
import { useAuthSetup } from './hooks/useAuthSetup'
import { useSetLanguagePreference } from './hooks/useSetLanguagePreference'
import { useAnalytics } from './hooks/useAnalytics'
import { useReceivePlayerFilter } from './hooks/useReceivePlayerFilter'
import { Loading } from './components/loading'
import { useRouteUtils } from './hooks/useRouteUtils'
import { useOpenSubscribeModalOnce } from './hooks/useOpenSubscribeModalOnce'
import { useBraze } from './hooks/useBraze'
import { CookiesToast } from './components/consent/CookieConsent'
// import { CookiesToast } from './components/consent/CookieConsent'
import PolicyModal from './components/modal/TOSModal'
import { authSelector } from './pages/auth/selectors'
import { bootstrapDuprApi } from './app/commons/network/duprApiWeb'
import { TwoFactor } from '@/pages/user'
import { setStrongMfaSetupRequired } from './pages/auth/authSlice'
import { ROUTE_DASHBOARD } from './constants/Routes'
import { useNavigate } from 'react-router-dom'

bootstrapDuprApi()

function App() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { user, verifyEmailModalState, isLoading } = useAppSelector(userSelector)
  const { token, strongMfaSetupRequired } = useAppSelector(authSelector)
  const { openSubscribeModal, handleSubscribeModalClose } = useOpenSubscribeModalOnce()

  useAnalytics()
  useAuthSetup()
  useSetBanner()
  useReceivePlayerFilter()
  useRouteUtils()
  useSetLanguagePreference()
  useBraze()

  useEffect(() => {
    // Don't touch RevenueCat / entitlements (or anything else) while the account still owes
    // strong-MFA setup — it isn't fully authenticated yet, and every backend call would 428.
    if (user && !strongMfaSetupRequired) {
      const RCPurchase = new RCPurchaseController()
      resolveRcAppUserId(user.id)
        .then((appUserId) => RCPurchase.initialize(appUserId))
        // Backend (/subscription/active) is the source of truth -- userId-keyed and merged
        // across the user's RC customer ids, so it stays correct through the app_user_id
        // migration. The RC SDK read is kept as an optimistic overlay for a just-purchased
        // session (before the webhook lands). Union: entitled via either source = entitled.
        .then(() =>
          Promise.all([
            RCPurchase.getEntitlements().catch(() => [] as string[]),
            fetchBackendEntitlementMarkers(),
          ])
        )
        .then(([sdkEntitlements, backendMarkers]) => {
          dispatch(setEntitlements(Array.from(new Set([...sdkEntitlements, ...backendMarkers]))))
        })
        .catch(() => {
          dispatch(setEntitlements([]))
        })
    }
  }, [user, strongMfaSetupRequired])

  if (isLoading) {
    return <Loading />
  }

  // A session that still owes strong-MFA setup is NOT fully authenticated. Render ONLY the enrolment
  // gate — no AppRoutes, no modals, no background effects — so nothing else mounts or calls the API
  // (every such call would 428, and things like the TOS-acceptance modal would trap the user with an
  // Accept that can never succeed). Enrolling a strong factor clears the flag (via the Continue
  // action below, and authoritatively when the next profile fetch succeeds) and enters the app.
  if (token && strongMfaSetupRequired) {
    return (
      <TwoFactor
        variant="onboarding"
        onEnrolled={() => {
          // Clearing the flag makes the session fully authenticated; useAuthSetup reacts by
          // fetching the profile (the authoritative confirmation — a 428 would re-raise the gate).
          dispatch(setStrongMfaSetupRequired(false))
          navigate(ROUTE_DASHBOARD)
        }}
      />
    )
  }

  return (
    <>
      <AppRoutes />
      <VerifyEmailModal open={verifyEmailModalState} />
      <SubscribeModal open={openSubscribeModal} onClose={handleSubscribeModalClose} />
      <CookiesToast />
      {user && token && !verifyEmailModalState ? <PolicyModal /> : null}
    </>
  )
}

export default App
